import { memo, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Loader, ViewportContext } from "@monorepo/components";
import { routeConfig } from "@monorepo/constants/routeConfig";
import { useServicesByCategoryQuery, useServicesQuery } from "@monorepo/services/api/services";
import theme from "@monorepo/styles/theme";
import { Box, Grow, List, ListItem } from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import { ServiceListAutocompleteContext } from "./ServiceListAutocomplete";
import { ServiceListAutocompleteItem } from "./ServiceListAutocompleteItem";

const minInputLength = 3;
const defaultServicesLength = 19;
const defaultViewCount = 50;
const moreServicesLength = 100;

const isMatch = ({ keywords, name, searchValue }: { keywords: string; name: string; searchValue: string }) => {
  if (searchValue) {
    try {
      const trimmedSearchValue = searchValue.trim().toLowerCase();

      return keywords.toLowerCase().match(trimmedSearchValue) || name.toLowerCase().match(trimmedSearchValue);
    } catch {
      return false;
    }
  }
  return false;
};

interface IProps {
  divided?: boolean;
  filterId?: number;
  searchValue: string;
  debouncedValue: string;
  setDebouncedValue?: React.Dispatch<React.SetStateAction<string>>;
  onServiceSelect: (id: string, name: string) => void;
}

export const ServiceListAutocompleteResult: React.FC<IProps> = memo(
  ({ divided = false, searchValue, debouncedValue, onServiceSelect, filterId, setDebouncedValue }) => {
    const { setServicesCount, setSearchValue } = useContext(ServiceListAutocompleteContext);
    const { data, isLoading } = useServicesQuery();
    const { data: filteredData, isLoading: isFilteredDataLoading } = useServicesByCategoryQuery(filterId, {
      skip: !filterId,
    });
    const [showAll, setShowAll] = useState<boolean>(true);
    const [viewServiceCount, setViewServiceCount] = useState<number>(defaultViewCount);
    const { isThirdParty } = useContext(ViewportContext);

    const services =
      useMemo(
        // eslint-disable-next-line max-len
        () => {
          if (searchValue && !debouncedValue) {
            return data?.filter(({ keywords, api_service_name: name }) => isMatch({ keywords, name, searchValue }));
          } else if (showAll || !debouncedValue) {
            if (!filterId) {
              return data?.slice(0, viewServiceCount);
            }
            return filteredData?.slice(0, viewServiceCount);
          } else if (debouncedValue) {
            if (!filterId) {
              return data?.filter(({ keywords, api_service_name: name }) =>
                isMatch({ keywords, name, searchValue: debouncedValue })
              );
            }
            return filteredData?.filter(({ keywords, api_service_name: name }) =>
              isMatch({ keywords, name, searchValue: debouncedValue })
            );
          }
        },
        [data, debouncedValue, filterId, filteredData, showAll, viewServiceCount, searchValue]
      ) || [];

    const { t } = useTranslation();
    const history = useHistory();

    // this use effect is needed when navigating between different services in ServicesList
    useEffect(() => {
      if (filterId) {
        setShowAll(false);
        setViewServiceCount(defaultServicesLength);
        setServicesCount(defaultServicesLength);
        if (!!setDebouncedValue) {
          setDebouncedValue("");
        }
      }
    }, [filterId, setDebouncedValue, setServicesCount]);

    useEffect(() => {
      if (searchValue.length < minInputLength && !showAll) {
        setShowAll(false);
        setViewServiceCount(defaultServicesLength);
        setServicesCount(defaultServicesLength);
      }
      if (searchValue.length > 0) {
        setShowAll(false);
        setViewServiceCount(defaultServicesLength);
        setServicesCount(defaultServicesLength);
      }
    }, [searchValue.length, services.length, setServicesCount, showAll]);

    if (!divided && searchValue.length < minInputLength && !showAll) {
      return null;
    }

    return (
      <Box
        sx={{
          marginTop: divided ? theme.spacing(5) : theme.spacing(1),
          borderRadius: theme.shape.borderRadius,
          height: "auto",
          maxHeight: divided ? "45vh" : "600px",
          overflow: "auto",
          padding: divided ? "0" : "inherit",
          width: { xs: "100%", xxl: divided ? "100%" : "inherit" },
        }}
      >
        <List
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "auto",
            padding: "6px 6px",
            listStyle: "none",
            overflowY: "auto",
            maxHeight: divided ? "100%" : "600px",
            background: divided ? theme.palette.background.default : theme.palette.secondary.main,
          }}
          role="listbox"
          tabIndex={-1}
        >
          {!showAll &&
            searchValue.length >= minInputLength &&
            services.length === 0 &&
            !isLoading &&
            !isFilteredDataLoading && (
              <>
                <ListItem
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    textAlign="center"
                    component="span"
                    sx={{
                      alignSelf: "center",
                      justifySelf: "center",
                      color: theme.palette.text.secondary,
                      margin: theme.spacing(1),
                    }}
                  >
                    {t(isThirdParty ? "common.result_nothing_found_third_party" : "common.result_nothing_found")}
                  </Typography>
                </ListItem>
                <ListItem
                  sx={{
                    display: "flex",
                    alignItems: divided ? { xs: "stretch", sm: "center" } : "stretch",
                    flexDirection: divided ? { xs: "column", sm: "row" } : "column",
                    rowGap: theme.spacing(1),
                    columnGap: divided ? theme.spacing(1) : 0,
                    justifyContent: "center",
                  }}
                >
                  {!isThirdParty && (
                    <Button
                      variant="contained"
                      disableElevation
                      color="neutral"
                      onClick={() => {
                        history.push(routeConfig.dashboard.services.templates.self);
                      }}
                    >
                      {t("payments.direct_payment")}
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    disableElevation
                    onClick={() => {
                      setViewServiceCount(defaultViewCount);
                      setServicesCount(defaultViewCount);
                      setShowAll(true);
                      setSearchValue("");
                    }}
                  >
                    {t("payments.show_all")}
                  </Button>
                </ListItem>
              </>
            )}
          {(isLoading || isFilteredDataLoading) && <Loader />}
          <Grow mountOnEnter unmountOnExit in={0 < searchValue.length && searchValue.length < minInputLength}>
            <Typography
              textAlign="center"
              sx={{
                padding: theme.spacing(1, 0),
                borderBottom: `solid 1px ${theme.palette.azureishWhite.main}`,
              }}
              variant="body2"
              component="span"
            >
              {t("payments.min_three_chars")}
            </Typography>
          </Grow>
          {!isLoading &&
            !isFilteredDataLoading &&
            services
              .slice(0, viewServiceCount)
              .map(({ api_service_name: name, id, category_id: serviceId }, index) => (
                <ServiceListAutocompleteItem
                  key={id}
                  name={name}
                  id={id}
                  serviceId={serviceId}
                  index={index}
                  onServiceSelect={() => onServiceSelect(id, name)}
                />
              ))}
          {((!isLoading && showAll) || services.length > viewServiceCount) && (
            <Button
              variant="outlined"
              sx={{
                margin: theme.spacing(1, 0),
                alignSelf: divided ? "center" : "stretch",
              }}
              onClick={() => {
                setServicesCount(viewServiceCount + moreServicesLength);
                setViewServiceCount((prevState) => prevState + moreServicesLength);
              }}
            >
              {t("payments.show_more")}
            </Button>
          )}
        </List>
      </Box>
    );
  }
);
